import React, { useState, useContext, useRef, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { DirectionContext } from "../App";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";
import { updateTermsAgreement } from "../utils/terms";

const ScreenshotSubmission = () => {
  const { navigateTo } = useContext(DirectionContext);
  const fileInputRef1 = useRef(null);
  const fileInputRef2 = useRef(null);
  const fileInputRef3 = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState({
    file1: null,
    file2: null,
    file3: null,
  });
  const [activeSection, setActiveSection] = useState(1); // 1, 2, 3
  const [disabledSections, setDisabledSections] = useState([]); // 비활성화된 섹션들
  const [showExampleSection, setShowExampleSection] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [applicationNumber, setApplicationNumber] = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const [isCreatingApplication, setIsCreatingApplication] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");

  // 페이지 로드 시 스크롤 위치를 맨 위로 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // 쿠키에서 applicationNumber와 applicationId 로드
    const cookieApplicationNumber = Cookies.get("application_number");
    const cookieApplicationId = Cookies.get("application_id");
    if (cookieApplicationNumber) {
      setApplicationNumber(cookieApplicationNumber);
    }
    if (cookieApplicationId) {
      setApplicationId(cookieApplicationId);
    }
  }, []);

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 파일 선택 핸들러
  const handleFileSelect = (section, e) => {
    const file = e.target.files[0];
    if (file) {
      const fileKey = `file${section}`;
      // 기존 업로드된 이미지가 있다면 메모리에서 해제
      if (uploadedFiles[fileKey] && uploadedFiles[fileKey].preview) {
        URL.revokeObjectURL(uploadedFiles[fileKey].preview);
      }

      setUploadedFiles((prev) => ({
        ...prev,
        [fileKey]: {
          file,
          preview: URL.createObjectURL(file),
        },
      }));

      setUploadError("");
    }
  };

  // 파일 업로드 버튼 클릭
  const handleUploadClick = (section) => {
    if (section === 1) fileInputRef1.current?.click();
    else if (section === 2) fileInputRef2.current?.click();
    else if (section === 3) fileInputRef3.current?.click();
  };

  // 파일 삭제
  const handleRemoveFile = (section) => {
    const fileKey = `file${section}`;
    if (uploadedFiles[fileKey] && uploadedFiles[fileKey].preview) {
      URL.revokeObjectURL(uploadedFiles[fileKey].preview);
    }
    setUploadedFiles((prev) => ({
      ...prev,
      [fileKey]: null,
    }));
  };

  // 증거가 없습니다 버튼 클릭
  const handleNoEvidence = (section) => {
    // 현재 섹션을 비활성화 목록에 추가
    setDisabledSections((prev) => [...prev, section]);

    // 다음 섹션 활성화
    if (section === 1) {
      setActiveSection(2);
    } else if (section === 2) {
      setActiveSection(3);
    }
  };

  // 섹션 제목 클릭 핸들러 (아래에서 위로만 이동 가능)
  const handleSectionClick = (section) => {
    // 현재 활성화된 섹션보다 작거나 같은 섹션만 선택 가능 (아래에서 위로만)
    if (section <= activeSection) {
      setActiveSection(section);
      // 선택한 섹션보다 아래 섹션들을 비활성화 목록에서 제거
      setDisabledSections((prev) => prev.filter((s) => s < section));
    }
  };

  // 신청서 생성 (첫 번째 파일 업로드 전에 호출)
  const createApplication = async () => {
    if (applicationNumber && applicationId) {
      return { applicationNumber, applicationId }; // 이미 생성된 경우 반환
    }

    setIsCreatingApplication(true);
    setUploadError("");

    try {
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      // 사용자 정보 가져오기 (clients/me GET 요청)
      const userInfoResponse = await fetch(
        `${API_ENDPOINTS.BASE_URL}/clients/me`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (userInfoResponse.ok) {
        const userInfoData = await userInfoResponse.json();

        // 이미 신청 완료한 집단소송이 있는지 확인 (쿠팡 소송만 체크)
        const hasCompletedApplication =
          userInfoData.applications &&
          userInfoData.applications.some(
            (app) => app.lawsuitId === 2 && app.status === "PAYMENT_COMPLETED",
          );

        // 전화번호 정규화 (하이픈, 공백 제거)
        const normalizedPhone =
          userInfoData.phoneNumber?.replace(/[\s-]/g, "") || "";

        if (hasCompletedApplication && normalizedPhone !== "01066076676") {
          // 이미 집단소송 신청을 완료한 사용자 (쿠팡 소송만 체크)
          // 단, 전화번호가 01066076676인 경우는 제외
          setIsCreatingApplication(false);
          setModalType("already-completed");
          setShowModal(true);
          return;
        }
      }

      // 기본 요청 본문 구성
      const baseRequestBody = {
        lawsuitId: 2,
        isUsimReplaced: false,
        damageDescription: "개인정보 유출로 인한 보이스피싱 피해를 입었습니다.",
        bankCode: Cookies.get("bankCode"),
        accountNumber: Cookies.get("accountNumber"),
        accountHolder: Cookies.get("accountHolder"),
      };

      // 신청 유형 확인
      const applicationType = Cookies.get("applicationType") || "self";
      let response;

      if (applicationType === "representative") {
        // 대표자 요청 본문 구성 (coSigners 배열 포함)
        const coOwnersJson = Cookies.get("coOwners");
        let coOwners = [];

        if (coOwnersJson) {
          try {
            coOwners = JSON.parse(coOwnersJson);
          } catch (e) {}
        }

        const requestBody = {
          ...baseRequestBody,
          baseFee: 10000,
          contingencyFeePercentage: 10,
          applicationType: "REPRESENTATIVE",
          // 백엔드 키 변경: coSigners → coSigers (오타지만 백엔드 스펙에 맞춤)
          coSigers: coOwners.map((owner) => ({
            name: owner.name,
            phone: owner.phone,
          })),
        };

        response = await fetch(`${API_ENDPOINTS.BASE_URL}/applications`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });
      } else {
        // API 호출 (본인 신청) - coSigners 제외하고 requestBody와 동일한 구조로 전송
        const requestBody = {
          ...baseRequestBody,
          baseFee: 10000,
          contingencyFeePercentage: 10,
          applicationType: "INDIVIDUAL",
        };

        response = await fetch(`${API_ENDPOINTS.BASE_URL}/applications`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();

        // 409 에러 처리 (공동신청자가 이미 결제 완료한 신청서가 있는 경우)
        if (response.status === 409) {
          throw new Error(
            errorData.message ||
              "공동신청자 중 이미 결제 완료한 신청서가 있는 사용자가 있습니다. 공동신청자 목록을 확인해주세요.",
          );
        }

        throw new Error(errorData.message || "제출에 실패했습니다.");
      }

      // 응답 데이터 받기
      const responseData = await response.json();
      // 새 응답 형태: { success, message, data: { id, applicationNumber } }
      const created = responseData.data || responseData;

      // applicationNumber 추출 (응답 구조에 따라 다르게 처리)
      const appNumber =
        created.applicationNumber ||
        responseData.applicationNumber ||
        responseData.application?.applicationNumber;

      if (!appNumber) {
        throw new Error(
          "신청번호를 받지 못했습니다. 나중에 다시 시도해주세요.",
        );
      }

      // applicationId 추출 (응답의 applicationId 값 또는 id 값)
      const appId =
        created.id ||
        created.applicationId ||
        responseData.applicationId ||
        responseData.id ||
        responseData.application?.applicationId ||
        responseData.application?.id;
      if (!appId) {
        throw new Error(
          "신청서 ID를 받지 못했습니다. 나중에 다시 시도해주세요.",
        );
      }

      // applicationNumber 쿠키에 저장
      Cookies.set("application_number", appNumber, {
        expires: 7, // 7일 유효
        path: "/",
      });

      // applicationId 쿠키에 저장 (다른 페이지에서 사용하기 위해)
      Cookies.set("application_id", appId, {
        expires: 7, // 7일 유효
        path: "/",
      });

      // applicationId 상태에 저장
      setApplicationId(appId);

      // 본인인증 단계에서 localStorage에 저장한 약관 동의(개인정보/이용약관/마케팅)를 서버에 반영
      try {
        const storedTerms = localStorage.getItem("termsAgreements");
        if (storedTerms) {
          await updateTermsAgreement(JSON.parse(storedTerms), appId);
        }
      } catch (termsError) {
        console.error("초기 약관 동의 업데이트 오류:", termsError);
      }

      // 가격 정보를 쿠키에 저장 (응답에서 받아온 값 또는 기본값)
      const baseFee =
        responseData.baseFee || responseData.application?.baseFee || 10000;
      const contingencyFeePercentage =
        responseData.contingencyFeePercentage ||
        responseData.application?.contingencyFeePercentage ||
        10;

      Cookies.set("lawsuit_amount", baseFee, {
        expires: 7,
        path: "/",
      });
      Cookies.set("lawsuit_fee", baseFee, {
        expires: 7,
        path: "/",
      });
      Cookies.set("lawsuit_success_fee_percentage", contingencyFeePercentage, {
        expires: 7,
        path: "/",
      });

      // 공동 신청자 정보 저장 (coSigners 또는 coApplicants)
      const coSigners = responseData.coSigners || responseData.coApplicants;
      if (coSigners && coSigners.length > 0) {
        Cookies.set(
          "coApplicantIds",
          JSON.stringify(coSigners.map((cs) => cs.id)),
          {
            expires: 7,
            path: "/",
          },
        );
      }

      setApplicationNumber(appNumber);
      setApplicationId(appId);
      return { applicationNumber: appNumber, applicationId: appId };
    } catch (error) {
      console.error("신청서 생성 오류:", error);
      setUploadError(
        error.message || "신청서 생성에 실패했습니다. 다시 시도해주세요.",
      );
      throw error;
    } finally {
      setIsCreatingApplication(false);
    }
  };

  // 제출하기 버튼 클릭
  const handleSubmit = async (section) => {
    const fileKey = `file${section}`;
    const file = uploadedFiles[fileKey];
    if (!file) return;

    setIsUploading(true);
    setUploadError("");

    try {
      // 1. 먼저 신청서 생성 (항상 먼저 실행)
      let currentApplicationId = applicationId;

      if (!applicationNumber || !currentApplicationId) {
        const result = await createApplication();
        // createApplication이 반환한 값으로 상태 업데이트
        if (result && result.applicationId) {
          currentApplicationId = result.applicationId;
          setApplicationId(result.applicationId);
        } else {
          throw new Error("신청서 생성에 실패했습니다. 다시 시도해주세요.");
        }
      }

      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      if (!currentApplicationId) {
        throw new Error("신청서 ID가 없습니다. 다시 시도해주세요.");
      }

      const formData = new FormData();
      formData.append("file", file.file);

      // 2. 새로운 API 엔드포인트로 파일 업로드
      // POST /uploads/lawsuit/{lawsuitCode}/{documentType}?applicationId={id}
      // lawsuitCode와 documentType은 path parameter, applicationId는 query parameter
      const uploadUrl = `${API_ENDPOINTS.BASE_URL}/uploads/lawsuit/coupang/membership-proof?applicationId=${currentApplicationId}`;

      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.status !== 201) {
        const errorData = await response.json();
        throw new Error(errorData.message || "스크린샷 업로드에 실패했습니다.");
      }

      const responseData = await response.json();
      if (responseData.fileKey) {
        Cookies.set("screenshot_fileKey", responseData.fileKey, {
          expires: API_ENDPOINTS.COOKIE_CONFIG.EXPIRES.ACCESS_TOKEN,
          ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
        });
      }

      navigateTo("/news-proof");
    } catch (error) {
      setUploadError(error.message || "파일 업로드 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
    }
  };

  // 완료하기 버튼 클릭
  const handleComplete = () => {
    // 활성화된 섹션에서 파일이 있으면 제출
    const activeFile = uploadedFiles[`file${activeSection}`];
    if (activeFile) {
      handleSubmit(activeSection);
    }
  };

  // 현재 활성화된 섹션에 파일이 있는지 확인
  const hasActiveFile = () => {
    return uploadedFiles[`file${activeSection}`] !== null;
  };

  // 이미지 확대 보기
  const handleImageEnlarge = (imageSrc) => {
    setEnlargedImage(imageSrc);
  };

  // 확대 이미지 닫기
  const handleCloseEnlarged = () => {
    setEnlargedImage(null);
  };

  // 모달 확인 버튼
  const handleModalConfirm = () => {
    setShowModal(false);

    // 이미 완료한 사용자인 경우 select 페이지로 이동
    if (modalType === "already-completed") {
      Cookies.remove(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      navigateTo("/select");
    }
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <Header>
            <SubTitle>쿠팡 가입 인증 자료 업로드</SubTitle>
            <Description>
              아래 자료 중 한 가지만 업로드하여 가입 내역을 인증해주세요
            </Description>
          </Header>

          {/* 첫 번째 업로드 섹션 */}
          <UploadSection
            isActive={activeSection >= 1 && !disabledSections.includes(1)}
          >
            <SectionHeader onClick={() => handleSectionClick(1)}>
              <SectionTitle>유출 안내에 관한 문자/이메일</SectionTitle>
              <SectionSubTitle>
                개인정보 유출 안내 문자 또는 이메일 캡쳐
              </SectionSubTitle>
            </SectionHeader>
            {activeSection >= 1 && !disabledSections.includes(1) && (
              <>
                <UploadArea
                  onClick={() => handleUploadClick(1)}
                  hasFile={uploadedFiles.file1 !== null}
                >
                  {uploadedFiles.file1 ? (
                    <UploadedImagePreview>
                      <PreviewImage
                        src={uploadedFiles.file1.preview}
                        alt="업로드된 이미지"
                      />
                      <RemoveButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(1);
                        }}
                      >
                        <RemoveIcon src="/png/delete.png" alt="삭제" />
                      </RemoveButton>
                    </UploadedImagePreview>
                  ) : (
                    <UploadPlaceholder>
                      이곳에 파일을 업로드해주세요
                    </UploadPlaceholder>
                  )}
                </UploadArea>
                <ActionButtons>
                  <NoEvidenceButton onClick={() => handleNoEvidence(1)}>
                    증거가 없습니다
                  </NoEvidenceButton>
                  <SubmitButton
                    onClick={() => handleSubmit(1)}
                    disabled={
                      !uploadedFiles.file1 ||
                      isUploading ||
                      isCreatingApplication
                    }
                  >
                    {isCreatingApplication
                      ? "신청서 생성 중..."
                      : isUploading
                        ? "업로드 중..."
                        : "제출하기"}
                  </SubmitButton>
                </ActionButtons>
              </>
            )}
            <HiddenFileInput
              type="file"
              ref={fileInputRef1}
              onChange={(e) => handleFileSelect(1, e)}
              accept="image/*"
            />
          </UploadSection>

          {/* 두 번째 업로드 섹션 */}
          <UploadSection
            isActive={activeSection >= 2 && !disabledSections.includes(2)}
          >
            <SectionHeader onClick={() => handleSectionClick(2)}>
              <SectionTitle>현재 쿠팡 회원 정보 캡쳐 화면</SectionTitle>
              <SectionSubTitle>
                문자가 없을 시 제출 (마이페이지 회원정보)
              </SectionSubTitle>
            </SectionHeader>
            {activeSection >= 2 && !disabledSections.includes(2) && (
              <>
                <UploadArea
                  onClick={() => handleUploadClick(2)}
                  hasFile={uploadedFiles.file2 !== null}
                >
                  {uploadedFiles.file2 ? (
                    <UploadedImagePreview>
                      <PreviewImage
                        src={uploadedFiles.file2.preview}
                        alt="업로드된 이미지"
                      />
                      <RemoveButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(2);
                        }}
                      >
                        <RemoveIcon src="/png/delete.png" alt="삭제" />
                      </RemoveButton>
                    </UploadedImagePreview>
                  ) : (
                    <UploadPlaceholder>
                      이곳에 파일을 업로드해주세요
                    </UploadPlaceholder>
                  )}
                </UploadArea>
                <ActionButtons>
                  <NoEvidenceButton onClick={() => handleNoEvidence(2)}>
                    증거가 없습니다
                  </NoEvidenceButton>
                  <SubmitButton
                    onClick={() => handleSubmit(2)}
                    disabled={
                      !uploadedFiles.file2 ||
                      isUploading ||
                      isCreatingApplication
                    }
                  >
                    {isCreatingApplication
                      ? "신청서 생성 중..."
                      : isUploading
                        ? "업로드 중..."
                        : "제출하기"}
                  </SubmitButton>
                </ActionButtons>
              </>
            )}
            <HiddenFileInput
              type="file"
              ref={fileInputRef2}
              onChange={(e) => handleFileSelect(2, e)}
              accept="image/*"
            />
          </UploadSection>

          {/* 세 번째 업로드 섹션 */}
          <UploadSection
            isActive={activeSection >= 3 && !disabledSections.includes(3)}
          >
            <SectionHeader onClick={() => handleSectionClick(3)}>
              <SectionTitle>최근 구매 내역 이메일</SectionTitle>
              <SectionSubTitle>
                주문 확인 이메일을 업로드 해주세요
              </SectionSubTitle>
            </SectionHeader>
            {activeSection >= 3 && !disabledSections.includes(3) && (
              <>
                <UploadArea
                  onClick={() => handleUploadClick(3)}
                  hasFile={uploadedFiles.file3 !== null}
                >
                  {uploadedFiles.file3 ? (
                    <UploadedImagePreview>
                      <PreviewImage
                        src={uploadedFiles.file3.preview}
                        alt="업로드된 이미지"
                      />
                      <RemoveButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(3);
                        }}
                      >
                        <RemoveIcon src="/png/delete.png" alt="삭제" />
                      </RemoveButton>
                    </UploadedImagePreview>
                  ) : (
                    <UploadPlaceholder>
                      이곳에 파일을 업로드해주세요
                    </UploadPlaceholder>
                  )}
                </UploadArea>
                <ActionButtons>
                  <SubmitButton
                    onClick={() => handleSubmit(3)}
                    disabled={
                      !uploadedFiles.file3 ||
                      isUploading ||
                      isCreatingApplication
                    }
                    style={{ width: "100%" }}
                  >
                    {isCreatingApplication
                      ? "신청서 생성 중..."
                      : isUploading
                        ? "업로드 중..."
                        : "제출하기"}
                  </SubmitButton>
                </ActionButtons>
              </>
            )}
            <HiddenFileInput
              type="file"
              ref={fileInputRef3}
              onChange={(e) => handleFileSelect(3, e)}
              accept="image/*"
            />
          </UploadSection>

          {/* 캡쳐 예시 화면 아코디언 */}
          <ExampleSection>
            <ExampleHeader
              onClick={() => setShowExampleSection(!showExampleSection)}
            >
              <div>
                <ExampleTitle>캡쳐 예시 화면</ExampleTitle>
                <ExampleSubTitle>
                  클릭 시 큰 화면으로 확인할 수 있습니다
                </ExampleSubTitle>
              </div>
              <ArrowIcon
                src="/png/arrow.png"
                alt="화살표"
                isOpen={showExampleSection}
              />
            </ExampleHeader>
            <AnimatePresence>
              {showExampleSection && (
                <ExampleContent
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ExampleImages>
                    <ExampleImage
                      src="/png/screenshot1.png"
                      alt="예시 이미지 1"
                      onClick={() => handleImageEnlarge("/png/screenshot1.png")}
                    />
                    <ExampleImage
                      src="/png/screenshot2.png"
                      alt="예시 이미지 2"
                      onClick={() => handleImageEnlarge("/png/screenshot2.png")}
                    />
                    <ExampleImage
                      src="/png/screenshot3.png"
                      alt="예시 이미지 3"
                      onClick={() => handleImageEnlarge("/png/screenshot3.png")}
                    />
                  </ExampleImages>
                </ExampleContent>
              )}
            </AnimatePresence>
          </ExampleSection>

          {uploadError && <ErrorAlert>{uploadError}</ErrorAlert>}

          <CompleteButton
            onClick={handleComplete}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={!hasActiveFile() || isUploading || isCreatingApplication}
          >
            {isCreatingApplication
              ? "신청서 생성 중..."
              : isUploading
                ? "업로드 중..."
                : "완료하기"}
          </CompleteButton>
        </Content>
      </Container>

      {/* 확대된 이미지 모달 */}
      <AnimatePresence>
        {enlargedImage && (
          <EnlargedImageModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseEnlarged}
          >
            <EnlargedImageWrapper onClick={(e) => e.stopPropagation()}>
              <EnlargedImage src={enlargedImage} alt="확대된 이미지" />
            </EnlargedImageWrapper>
          </EnlargedImageModal>
        )}
      </AnimatePresence>

      {/* 완료 모달 */}
      {showModal && (
        <ModalOverlay>
          <ModalContainer>
            <ModalContent>
              {modalType === "already-completed" ? (
                <ModalText>
                  이미 집단소송 신청을 완료하셨습니다.
                  <br />
                  소송 진행 상황은 메인 페이지에서 확인하실 수 있습니다.
                </ModalText>
              ) : null}
              <ModalButton onClick={handleModalConfirm}>확인</ModalButton>
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f6f8ff;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #f6f8ff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f6f8ff;

  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 24px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 0px;
  line-height: 1.4;
`;

const UploadSection = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  opacity: ${(props) => (props.isActive ? 1 : 0.5)};
  transition: opacity 0.3s ease;
`;

const SectionHeader = styled.div`
  cursor: pointer;
  margin-bottom: 16px;
  pointer-events: auto;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const SectionSubTitle = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 0px;
  line-height: 1.4;
`;

const UploadArea = styled.div`
  width: 100%;
  min-height: 120px;
  border: 2px dashed ${(props) => (props.hasFile ? "#0075ff" : "#e5e5e5")};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: ${(props) => (props.hasFile ? "#f5f5f5" : "white")};
  transition: all 0.3s ease;
  margin-bottom: 16px;

  &:hover {
    border-color: #0075ff;
    background-color: #f9f9f9;
  }
`;

const UploadPlaceholder = styled.div`
  color: #999;
  font-size: 16px;
  text-align: center;
`;

const UploadedImagePreview = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: transparent;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 0;
  z-index: 10;
`;

const RemoveIcon = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const NoEvidenceButton = styled.button`
  flex: 1;
  height: 48px;
  border-radius: 8px;
  border: 1px solid #e8eef5;
  background-color: #f8fafe;
  color: #1d3557;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f0f4f9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  height: 48px;
  border-radius: 8px;
  border: none;
  background-color: ${(props) => (props.disabled ? "#A9DAFF" : "#0075ff")};
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) => (props.disabled ? "#A9DAFF" : "#0066df")};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ExampleSection = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  overflow: hidden;
`;

const ExampleHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  cursor: pointer;
  position: relative;
  padding-right: 30px;
`;

const ExampleTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin: 0 0 4px 0;
`;

const ExampleSubTitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const ArrowIcon = styled.img`
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.3s ease;
`;

const ExampleContent = styled(motion.div)`
  overflow: hidden;
  margin-top: 16px;
`;

const ExampleImages = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const ExampleImage = styled.img`
  flex: 1;
  max-width: calc(33.333% - 8px);
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const ErrorAlert = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const CompleteButton = styled(motion.button)`
  width: 100%;
  height: 56px;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#A9DAFF" : "#3855E2")};
  color: ${(props) => (props.disabled ? "#CCEAFF" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  margin-top: 0px;

  @media (min-width: 769px) {
    max-width: 470px;
  }
`;

// 확대 이미지 모달 관련 스타일
const EnlargedImageModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const EnlargedImageWrapper = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

const EnlargedImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
`;

// 모달 스타일 컴포넌트
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  touch-action: none;
`;

const ModalContainer = styled.div`
  width: 90%;
  max-width: 355px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #333;
  text-align: left;
  margin-bottom: 30px;
  line-height: 1.5;
  width: 100%;
`;

const ModalButton = styled.button`
  width: 100%;
  height: 50px;
  background-color: #0075ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  align-self: center;
`;

export default ScreenshotSubmission;
